/**
 * dexId-vs-region cross-check drop filter — b13c.7 / v1.4.7 build 1.
 *
 * The write key for a cell is the region DERIVED from its dex id (see
 * [[dexIdToRegion]]) — never the OCR screen-classifier's region guess. But the
 * classifier region is still a useful sanity signal: if the screen the user is
 * looking at clearly says "Sinnoh" while a cell decoded to dex #58 (Kanto), the
 * read is almost certainly a misread/mis-slotted digit, not a real cross-region
 * scroll. The build-196 device test (IMG_8769 / IMG_8770) is exactly this case:
 * a Sinnoh screenshot produced two Kanto-range writes that landed in the wrong
 * Perfect-dex slice.
 *
 * Rule (per the b13c.7 brief deliverable #4):
 *   - Derive Y = dexIdToRegion(dexId).
 *   - If the classifier region X and Y are the SAME → keep.
 *   - If X is unknown (classifier couldn't read a region) → keep: we have no
 *     contradicting signal, and the dex id is authoritative anyway.
 *   - If X and Y are ADJACENT regions (one National-Dex generation apart) →
 *     keep: a real scroll across a region boundary briefly shows the old
 *     header while the next generation's cells are already on screen.
 *   - Otherwise (X and Y are non-adjacent) → DROP the cell.
 *
 * Exception: Hisui (899-905) is interleaved inside Galar (810-898) in Pokémon
 * GO's sorting — a "Galar" classifier frame legitimately contains Hisui-range
 * cells (and vice-versa). That pair is always allowed regardless of adjacency.
 */

import { dexIdToRegion, type OcrRegion } from "./dexIdToRegion";
import type {
  PokedexRegionLite,
  PokedexVariant,
} from "./pogoPokedexVariantClassifier";

/**
 * Region order used to decide adjacency. Matches ascending National Dex
 * generations. "unidentified" (Meltan/Melmetal, 808-809) sits between Alola
 * and Galar so it is adjacent to both. "unknown" is intentionally absent — an
 * unknown classifier region never drops a cell (handled before this list).
 */
const REGION_ORDER: OcrRegion[] = [
  "kanto",
  "johto",
  "hoenn",
  "sinnoh",
  "unova",
  "kalos",
  "alola",
  "unidentified",
  "galar",
  "hisui",
  "paldea",
];

/**
 * Normalise a classifier region (the adapter's `PokedexRegionLite`) into the
 * `OcrRegion` vocabulary. They share every key except that `PokedexRegionLite`
 * has no "unidentified" bucket, so any classifier value maps 1:1 or to
 * "unknown".
 */
function classifierToOcrRegion(region: PokedexRegionLite): OcrRegion {
  switch (region) {
    case "kanto":
    case "johto":
    case "hoenn":
    case "sinnoh":
    case "unova":
    case "kalos":
    case "alola":
    case "galar":
    case "hisui":
    case "paldea":
      return region;
    case "unknown":
    default:
      return "unknown";
  }
}

function areAdjacent(a: OcrRegion, b: OcrRegion): boolean {
  const ia = REGION_ORDER.indexOf(a);
  const ib = REGION_ORDER.indexOf(b);
  if (ia < 0 || ib < 0) return false;
  return Math.abs(ia - ib) === 1;
}

/** True for the Galar↔Hisui interleave that PoGo's own sorting produces. */
function isHisuiGalarException(a: OcrRegion, b: OcrRegion): boolean {
  return (
    (a === "galar" && b === "hisui") ||
    (a === "hisui" && b === "galar")
  );
}

export interface CrossCheckResult {
  /** Whether the cell should be written. */
  keep: boolean;
  /** Region the dex id resolves to (the authoritative write key). */
  derivedRegion: OcrRegion;
  /** Region the OCR screen classifier reported (sanity signal only). */
  classifierRegion: OcrRegion;
  /** Why the cell was dropped, for diagnostics. Absent when kept. */
  reason?: "non_adjacent_region";
}

/**
 * Decide whether a single (dexId, classifierRegion) cell survives the
 * cross-check. Pure and total.
 */
export function crossCheckCell(
  dexId: number,
  classifierRegion: PokedexRegionLite,
  variant?: PokedexVariant | null,
): CrossCheckResult {
  const derivedRegion = dexIdToRegion(dexId);
  const classifier = classifierToOcrRegion(classifierRegion);

  // G-Max exemption: the G-Max roster legitimately spans every region
  // (Kanto #3/#6/#9, Alola #770, etc.). Any cross-region "mismatch" on a G-Max
  // page is expected, not a misread — keep the cell.
  if (variant === "gmax") {
    return { keep: true, derivedRegion, classifierRegion: classifier };
  }

  // No contradicting signal (or an exact match) — keep.
  if (classifier === "unknown" || classifier === derivedRegion) {
    return { keep: true, derivedRegion, classifierRegion: classifier };
  }
  // A dex id that resolves to nothing (out of roster) is dropped upstream by
  // the slot mapper; if one reaches here, treat it as non-contradicting so we
  // never throw — the store rejects ids < 1 anyway.
  if (derivedRegion === "unknown") {
    return { keep: true, derivedRegion, classifierRegion: classifier };
  }
  // PoGo interleaves Hisui inside Galar frames — always allowed.
  if (isHisuiGalarException(classifier, derivedRegion)) {
    return { keep: true, derivedRegion, classifierRegion: classifier };
  }
  // A real scroll across a single region boundary briefly mixes the two.
  if (areAdjacent(classifier, derivedRegion)) {
    return { keep: true, derivedRegion, classifierRegion: classifier };
  }
  // Non-adjacent mismatch — the read contradicts the screen. Drop it.
  return {
    keep: false,
    derivedRegion,
    classifierRegion: classifier,
    reason: "non_adjacent_region",
  };
}
